import { fastapi } from './api';
import { userFromToken } from '../contracts/authContracts';
import type { User } from '../contracts/authContracts';

export type { User } from '../contracts/authContracts';

export interface AuthServiceResponse {
  token?: string;
  access_token?: string;
  comercio_key?: string;
  timezone_iana?: string;
  requires_2fa?: boolean;
  onboarding_required?: boolean;
  email_sent?: boolean;
  message?: string;
}

interface RegisterPayload {
  email: string;
  password: string;
  comercio_key: string; // username del comercio
  nombre?: string;
  direccion?: string;
  telefono?: string;
  categoria?: string;
  descripcion?: string;
  timezone_iana?: string;
}

interface GoogleLoginPayload {
  credential: string;
  comercio_key?: string;
  timezone_iana?: string;
}

const persistAuth = (data: AuthServiceResponse) => {
  const tokenToSave = data.access_token || data.token;
  if (tokenToSave) {
    localStorage.setItem('jwtToken', tokenToSave);
    if (data.comercio_key) localStorage.setItem('comercio_key', data.comercio_key);
    try { localStorage.removeItem('comercioKey'); } catch { /* Best-effort cleanup. */ }
    if (data.timezone_iana) localStorage.setItem('timezone_iana', data.timezone_iana);
    if (data.onboarding_required) {
      localStorage.setItem('onboarding_done', 'false');
    }
    // Onboarding flows hydrate Redux explicitly and initialize chat only after
    // the server has recorded completion.
    if (!data.onboarding_required) {
      try { window.dispatchEvent(new Event('auth:login')); } catch { /* Best-effort cleanup. */ }
    }
  }
};

const authService = {
  register: async (payload: RegisterPayload): Promise<any> => {
    const response = await fastapi.post<AuthServiceResponse>('/auth/register', payload);
    return response.data;
  },

  login: async (email: string, password: string): Promise<AuthServiceResponse> => {
    const response = await fastapi.post<AuthServiceResponse>('/auth/login', { email, password });
    if (!response.data.requires_2fa) {
      persistAuth(response.data);
    }
    return response.data;
  },

  verifyEmail: async (token: string): Promise<any> => {
    const response = await fastapi.get(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  verify2FA: async (email: string, code: string): Promise<AuthServiceResponse> => {
    const response = await fastapi.post<AuthServiceResponse>('/auth/verify-2fa', { email, code });
    persistAuth(response.data);
    return response.data;
  },

  get2FASettings: async (): Promise<{ enabled: boolean }> => {
    const response = await fastapi.get('/auth/2fa/settings');
    return response.data;
  },

  update2FASettings: async (enabled: boolean): Promise<any> => {
    const response = await fastapi.put('/auth/2fa/settings', { enabled });
    return response.data;
  },

  completeOnboarding: async (): Promise<void> => {
    await fastapi.post('/auth/onboarding/complete', { completed: true });
  },

  loginWithGoogle: async (payload: GoogleLoginPayload): Promise<AuthServiceResponse> => {
    const response = await fastapi.post<AuthServiceResponse>('/auth/google/verify', payload);
    persistAuth(response.data);
    return response.data;
  },

  logout: (): void => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('comercioKey');
    localStorage.removeItem('comercio_key');
    localStorage.removeItem('timezone_iana');
    try { window.dispatchEvent(new Event('auth:logout')); } catch { /* Best-effort cleanup. */ }
  },

  getCurrentUser: (): User | null => {
    const token = localStorage.getItem('jwtToken');
    if (!token || token === 'null' || !token.trim()) return null;
    const timezone_iana = localStorage.getItem('timezone_iana') || 'UTC';
    const user = userFromToken(token, timezone_iana);
    if (!user) return null;

    // The signed token is authoritative; storage keeps only the API's snake_case key.
    localStorage.setItem('comercio_key', user.comercioKey);
    localStorage.removeItem('comercioKey');
    return user;
  },
};

export default authService;
